import React, {useMemo, useState} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {ReactComponent as ExternalLinkIcon} from 'assets/svg/external-link.svg';
import {ReactComponent as UnlockIcon} from 'assets/svg/unlock.svg';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Spinner} from 'components/ui-kit/Spinner';
import {isAdmin$, profile$} from 'models/auth';
import {$removeLockModalOpened, setRemoveLockModalOpened} from 'models/codeLocks';
import {commonDataState$} from 'models/products/detail';
import {UserPermission} from 'types';
import {BlockingType, BlokcingReason} from 'types/products-blocking';
import {CodeBlockingData} from 'types/products-detail';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {
    Card,
    CardAccent,
    CardBody,
    CardHeader,
    CardHeaderInfo,
    CardTitle,
    Container,
    DataLabel,
    DataList,
    DataRow,
    DataValue,
    Divider,
    DocumentLink,
    Grid,
    SectionTitle,
    SpinnerContainer,
    StatusBadge,
    StatusDot,
    UnblockButton,
} from './styled';
import {UnblockModal} from './UnblockModal';

const BlockingCard = ({
    blocking,
    onUnblock,
}: {
    blocking: CodeBlockingData;
    onUnblock: () => void;
}) => {
    const {t} = useTranslation();
    const isAdmin = useStore(isAdmin$);
    const profile = useStore(profile$);
    const transferDocumentUrl = useMemo(
        () =>
            blocking.sourceReportType === BlockingType.TRANSFER_DOCUMENT
                ? `/documents/transfer/${blocking.sourceReportId}`
                : `/invoices/${blocking.sourceReportId}`,
        [blocking.sourceReportId, blocking.sourceReportType],
    );

    const canUnblock = useMemo(() => {
        if (isAdmin) {
            return true;
        }

        if (blocking.organization === profile?.legalPerson?.company_name) {
            return true;
        }
        return false;
    }, [isAdmin]);

    return (
        <Card>
            {/* <CardAccent /> */}
            <CardBody>
                <CardHeader>
                    <CardHeaderInfo>
                        <CardTitle>{t('pages.products.detail.blocking.cardTitle')}</CardTitle>
                        {/* <StatusBadge>
                            <StatusDot />
                            {t('dictionaries.products.activeStatuses.ACTIVE')}
                        </StatusBadge> */}
                    </CardHeaderInfo>

                    <PermissionAccess permissions={[UserPermission.MPL_BLOCKING_CLOSE]}>
                        {canUnblock && (
                            <UnblockButton
                                type="button"
                                variant={ButtonVariant.Outline}
                                icon={<UnlockIcon width={14} height={14} />}
                                onClick={onUnblock}
                            >
                                {t('pages.products.detail.blocking.unblock')}
                            </UnblockButton>
                        )}
                    </PermissionAccess>
                </CardHeader>
                <Divider />
                <DataList>
                    <DataRow>
                        <DataLabel>{t('pages.products.detail.blocking.createdAt')}</DataLabel>
                        <DataValue>
                            {handleNil(blocking.createdAt, (value: string) =>
                                formatDate(value, 'dd.MM.yyyy, HH:mm:ss'),
                            )}
                        </DataValue>
                    </DataRow>
                    <DataRow>
                        <DataLabel>{t('pages.products.detail.blocking.reason')}</DataLabel>
                        <DataValue $danger>
                            {handleNil(blocking.reason, (value: string) =>
                                t(`dictionaries.products.blockingReason.${value}`),
                            )}
                        </DataValue>
                    </DataRow>
                    <DataRow>
                        <DataLabel>{t('pages.products.detail.blocking.scope')}</DataLabel>
                        <DataValue>
                            {handleNil(blocking.scopes?.length ? blocking.scopes : null, () =>
                                blocking.scopes
                                    .map((scope) =>
                                        t(`dictionaries.products.blockingScope.${scope}`),
                                    )
                                    .join(', '),
                            )}
                        </DataValue>
                    </DataRow>
                    <DataRow>
                        <DataLabel>{t('pages.products.detail.blocking.sourceFormation')}</DataLabel>
                        <DataValue>
                            {handleNil(blocking.sourceFormation, (value: string) =>
                                t(`dictionaries.products.blockingSourceFormation.${value}`),
                            )}
                        </DataValue>
                    </DataRow>
                    <DataRow>
                        <DataLabel>{t('pages.products.detail.blocking.organization')}</DataLabel>
                        <DataValue>{handleNil(blocking.organization)}</DataValue>
                    </DataRow>
                    {blocking.reason === BlokcingReason.RESERVED && (
                        <>
                            <Divider />
                            <DataRow>
                                <DataLabel>
                                    {t('pages.products.detail.blocking.transferDocument')}
                                </DataLabel>
                                <DataValue>
                                    {handleNil(blocking.sourceReportId, () => (
                                        <>
                                            <span>{blocking.sourceReportId}</span>
                                            <DocumentLink to={transferDocumentUrl} newTab>
                                                <ExternalLinkIcon width={10} height={10} />
                                                {t('pages.products.detail.blocking.open')}
                                            </DocumentLink>
                                        </>
                                    ))}
                                </DataValue>
                            </DataRow>
                        </>
                    )}
                </DataList>
            </CardBody>
        </Card>
    );
};

export const Blocking = () => {
    const {t} = useTranslation();
    const {data, isLoading} = useStore(commonDataState$);
    const removeLockModalOpened = useStore($removeLockModalOpened);

    if (isLoading) {
        return (
            <SpinnerContainer>
                <Spinner />
            </SpinnerContainer>
        );
    }

    return (
        <Container>
            <SectionTitle>{t('pages.products.detail.blocking.title')}</SectionTitle>
            <Grid>
                {data?.blockings.map((blocking) => (
                    <BlockingCard
                        key={blocking.blockingId}
                        blocking={blocking}
                        onUnblock={() => setRemoveLockModalOpened(blocking)}
                    />
                ))}
            </Grid>
            <UnblockModal
                show={Boolean(removeLockModalOpened)}
                markingCode={data?.markingCode}
                onClose={() => setRemoveLockModalOpened(null)}
            />
        </Container>
    );
};
